<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, watch,  } from 'vue';
import { useToast } from 'vue-toastification';
import { useMemberStore } from '@/stores/member';
import { trans } from 'laravel-vue-i18n';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const optionsStore = useOptionsStore();
const { genderOptions } = storeToRefs(optionsStore);

const props = defineProps({
    member: {
        type: Object,
        default: () => ({
            //
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const toast = useToast();
const { store, update } = useMemberStore();

const image = ref(null);
const imagePreviewUrl = ref(props.member.image_url || null);

const onImageChange = (e) => {
    const file = e.target.files?.[0];
    image.value = file;
    if (file) {
        imagePreviewUrl.value = URL.createObjectURL(file);
    }
};

const form = ref({
    hash: props.member?.hash,
    // user fields
    first_name: props.member?.user?.first_name ?? '',
    last_name: props.member?.user?.last_name ?? '',
    email: props.member?.user?.email ?? '',
    send_password: false,
    set_password: false,
    password: '',
    password_confirmation: '',
    // member fields
    phone_number: props.member?.phone_number ?? '',
    gender: props.member?.gender ?? '',
    dob: props.member?.dob ? (() => {
        const [year, month, day] = props.member.dob.split('-');
        return new Date(Number(year), Number(month) - 1, Number(day));
    })() : null,
});

watch(() => form.value.set_password, (on) => {
    if (!on) {
        form.value.password = '';
        form.value.password_confirmation = '';
    }
});

const save = async () => {
    if (form.value.dob) {
        form.value.dob = form.value.dob.toISOString().split('T')[0];
    }

    const formData = new FormData();
    Object.entries(form.value).forEach(([key, val]) => {
        if (val !== undefined && val !== null && typeof val !== 'function') {
            formData.append(key, val);
        }
    });
    if (image.value) {
        formData.append('image', image.value);
    }

    const action = props.mode === 'Edit' ? update : store;

    try {
        await action(formData);
        toast.success(trans('actions.update.success', { type: trans('label.member_profile') }));
        emit('close-modal');
    } catch (error) {
        toast.error(trans('actions.update.error', { type: trans('label.member_profile') }));
    }
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.mode_what', { 'mode' : mode, 'what' : 'member' }) }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { 'what' : 'member' }) }}</template>
    <div class="md:col-span-2 mb-6">
        <div
            class="flex flex-col sm:flex-row items-center sm:items-start justify-center gap-6 p-6 rounded-lg border border-dashed border-gray-300">
            <div v-if="imagePreviewUrl" class="flex-shrink-0">
                <img
                    :src="imagePreviewUrl"
                    class="w-24 h-24 rounded-full object-cover border border-gray-300 shadow-sm">
            </div>

            <div class="flex flex-col gap-2 text-center sm:text-left w-full sm:w-auto">
                <StyledFileSelector
                    name="image"
                    :label="imagePreviewUrl ? $t('label.replace_image') : $t('label.upload_image')"
                    accept="image/*"
                    class="w-60"
                    @change="onImageChange" />
            </div>
        </div>
    </div>
    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledInput 
                v-model="form.first_name"
                name="first_name"
                :label="$t('label.first_name')" 
                :clearable="false" />

            <StyledInput 
                v-model="form.last_name"
                name="last_name"
                :label="$t('label.last_name')" />

            <StyledInput 
                v-model="form.email"
                name="email"
                :label="$t('label.email')" />

            <StyledMask
                v-model="form.phone_number"
                name="phone_number"
                placeholder="###-###-####"
                :options="{ mask: '###-###-####' }"
                :label="$t('label.phone_number')" />

            <StyledDropdown
                v-model="form.gender"
                name="gender"
                :options="genderOptions"
                :label="$t('label.gender')" />

            <StyledDatepicker 
                v-model="form.dob"
                name="dob"
                :label="$t('label.date_of_birth')" />

            <StyledRoundSwitch
                v-model="form.send_password"
                :label="$t('label.email_password_setup')"
                inset
                name="send_password"
                @input="form.set_password = false" />

            <StyledRoundSwitch
                v-model="form.set_password"
                :label="$t('label.set_password')"
                inset
                name="set_password"
                @input="form.send_password = false" />
        </div>

        <div v-if="form.set_password" class="grid md:grid-cols-2 gap-x-4">
            <StyledPassword 
                v-model="form.password"
                name="password"
                :label="$t('label.password')" />

            <StyledPassword 
                v-model="form.password_confirmation"
                name="password_confirmation"
                :label="$t('label.confirm_password')" />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
